import * as THREE from "three";
import { WEAPONS, type WeaponConfig } from "@neon-purge/shared";
import { warmTexture } from "./renderer.js";
import { avatarMaps } from "./avatarTextures.js";
import { weaponMaps, type WeaponSurface } from "./weaponTextures.js";
import { muzzleFlashTexture } from "./combatTextures.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

/**
 * First-person weapon viewmodel: procedural low-poly guns built from boxes,
 * attached to the camera. Handles bob, ADS centering, recoil kick, muzzle
 * flash and the knife swing. Purely visual — the server owns all outcomes.
 */

/** The pattern families a part list may name explicitly (`plain` is the
 *  shape-inferred fallback, so it is never authored). */
type TextureKey = Exclude<WeaponSurface, "plain">;

export interface Part {
  p: [number, number, number];
  s: [number, number, number];
  c: string;
  t?: TextureKey;
}

const GUNMETAL = "#2a2f3d";
const DARK = "#1a1e28";
const GRIP = "#242833";
const ACCENT = "#00f0ff";
const WOOD = "#6f4a2f";
const HAZARD = "#f6b73c";
const RED = "#d84738";
const VIOLET = "#8f5bff";

// Weapon-part box geometries are immutable and identical for every build of a
// given weapon — share ONE instance per (w,h,d) module-wide (first-person
// viewmodel AND every avatar's third-person prop draw from this). Gun Game
// swaps weapons every kill; before this cache each swap re-generated and
// re-uploaded ~15-30 geometries. userData.shared guards them from deep-dispose.
const boxGeoCache = new Map<string, THREE.BufferGeometry>();
export function sharedBoxGeo(w: number, h: number, d: number): THREE.BufferGeometry {
  const key = `${w}|${h}|${d}`;
  let g = boxGeoCache.get(key);
  if (!g) {
    const r = Math.min(0.045, Math.min(w, h, d) * 0.32);
    g = r > 0.012 ? new RoundedBoxGeometry(w, h, d, 2, r) : new THREE.BoxGeometry(w, h, d);
    g.userData.shared = true;
    boxGeoCache.set(key, g);
  }
  return g;
}
const cylZGeoCache = new Map<string, THREE.BufferGeometry>();
function sharedCylinderZGeo(w: number, h: number, d: number): THREE.BufferGeometry {
  const r = Math.max(w, h) * 0.5;
  const key = `${r}|${d}`;
  let g = cylZGeoCache.get(key);
  if (!g) {
    g = new THREE.CylinderGeometry(r, r, d, 12).rotateX(Math.PI / 2);
    g.userData.shared = true;
    cylZGeoCache.set(key, g);
  }
  return g;
}
/** TAPERED limb segment along +Z, `rFar` at the +Z end (what `lookAt` aims at).
 *  Human forearms/upper arms are cones, not pipes — `sharedCylinderZGeo`'s
 *  constant radius is a big part of why the first-person arms read as plumbing.
 *  Cached by radii+length like its uniform sibling, so a weapon swap re-uses
 *  the same GPU buffer. */
const taperZGeoCache = new Map<string, THREE.BufferGeometry>();
function sharedTaperedZGeo(rNear: number, rFar: number, len: number): THREE.BufferGeometry {
  const key = `${rNear}|${rFar}|${len}`;
  let g = taperZGeoCache.get(key);
  if (!g) {
    // CylinderGeometry(radiusTop, radiusBottom, …); rotateX(+90°) maps +Y → +Z,
    // so `radiusTop` ends up at the +Z (far) end.
    g = new THREE.CylinderGeometry(rFar, rNear, len, 12).rotateX(Math.PI / 2);
    g.userData.shared = true;
    taperZGeoCache.set(key, g);
  }
  return g;
}

/**
 * THE FIRST-PERSON HAND (2026-08-01, mission `graphics`).
 *
 * These two objects are on screen for 100% of the match, 30 cm from the lens,
 * and until now each was three axis-aligned BOXES — a palm cube, a thumb cube
 * and one slab standing in for four fingers. Nothing else in the game is
 * looked at as much or was as crude, and no amount of surface work fixes a
 * silhouette that is literally a die.
 *
 * Built here as FOUR FINGERS with two phalanges each, a two-segment opposed
 * thumb, a palm with a knuckle ridge and a gauntlet cuff — 12 volumes, every
 * one elliptical in section, MERGED into a single geometry. Merging is what
 * makes the detail free: the whole hand is one draw call where the old three
 * boxes were three, so the viewmodel gets a real fist and LOSES four draws
 * (two hands × two meshes).
 *
 * Cached per side for the tab: the geometry is identical for every weapon (the
 * mesh is positioned and rotated per grip), so a weapon swap re-uses the same
 * GPU buffer — like every other geometry in this file.
 *
 * `side`: +1 right hand, −1 left. Fingers curl toward −Z (down-range), the
 * wrist is at +Z, and the origin is the centre of the palm so a caller can
 * place the hand by where the grip is.
 */
const fpHandCache = new Map<number, THREE.BufferGeometry>();
const _fpMat = new THREE.Matrix4();
const _fpEuler = new THREE.Euler();
const _fpQuat = new THREE.Quaternion();
const _fpPos = new THREE.Vector3();
const _fpScale = new THREE.Vector3(1, 1, 1);
export function fpHandGeo(side: number): THREE.BufferGeometry {
  const cached = fpHandCache.get(side);
  if (cached) return cached;
  const parts: THREE.BufferGeometry[] = [];
  const add = (
    w: number, h: number, d: number, s: number,
    px: number, py: number, pz: number,
    rx = 0, ry = 0, rz = 0,
  ): void => {
    const g = new RoundedBoxGeometry(w, h, d, 1, Math.min(0.02, Math.min(w, h, d) * 0.34));
    roundSectionXY(g, w, h, s);
    _fpPos.set(px, py, pz);
    _fpEuler.set(rx, ry, rz);
    _fpQuat.setFromEuler(_fpEuler);
    g.applyMatrix4(_fpMat.compose(_fpPos, _fpQuat, _fpScale));
    for (const k of Object.keys(g.attributes)) {
      if (k !== "position" && k !== "normal" && k !== "uv") g.deleteAttribute(k);
    }
    parts.push(g);
  };
  // palm: broad across the knuckles, thin front-to-back, with the knuckle line
  // standing slightly proud of the back of the hand
  add(0.098, 0.088, 0.115, 0.55, 0, 0, 0.01);
  add(0.100, 0.030, 0.040, 0.9, 0, 0.030, -0.045);        // knuckle ridge
  add(0.108, 0.078, 0.036, 0.85, 0, -0.004, 0.078);        // gauntlet cuff at the wrist
  // FOUR FINGERS, proximal + distal, curling under the grip. Lengths taper from
  // the index to the little finger, and each one is rotated slightly differently
  // so the fist is not a comb — the irregularity is most of the read.
  for (let i = 0; i < 4; i++) {
    const fx = side * (0.036 - i * 0.024);
    const len = 0.062 - i * 0.006;
    const curl = 1.05 + i * 0.06;
    add(0.021, len, 0.030, 0.95, fx, -0.052, -0.028 - i * 0.002, curl, 0, side * (i - 1.5) * 0.05);
    add(0.019, len * 0.82, 0.028, 0.95,
      fx, -0.052 - Math.cos(curl) * len * 0.5 - 0.018, -0.050 - i * 0.002, 2.05 + i * 0.05, 0, 0);
  }
  // THUMB: two segments wrapping the front of the grip from the inboard side —
  // the volume that says "closed around something" rather than "clenched".
  add(0.030, 0.058, 0.034, 0.9, -side * 0.048, -0.010, 0.020, -0.35, 0, side * 0.85);
  add(0.026, 0.050, 0.030, 0.9, -side * 0.062, -0.048, -0.014, -1.25, 0, side * 0.55);
  const merged = mergeGeometries(parts) ?? parts[0];
  for (const g of parts) if (g !== merged) g.dispose();
  merged.userData.shared = true;
  fpHandCache.set(side, merged);
  return merged;
}

/**
 * Bend a weapon part's SQUARE cross-section into an ELLIPTICAL one, in the XY
 * plane (the part's long axis is Z — muzzle-forward). This is the avatar's
 * `roundCrossSection` rotated into the gun's frame, and it is here for the same
 * reason (2026-08-01, mission `graphics`): a receiver, a magazine and a pistol
 * grip are all machined/moulded volumes with an oval section, and every one of
 * them was a chamfered CUBE. A rounded-corner cube in a hand still reads as a
 * block, which is the loudest remaining Minecraft signal on the object that is
 * on screen in literally every frame of the game.
 *
 *     X = u·√(1 − v²/2),  Y = v·√(1 − u²/2)      (u = x/hx, v = y/hy ∈ [−1,1])
 *
 * blended by `s` (0 = untouched box, 1 = full ellipse). Pure vertex-buffer
 * edit: same mesh count, same materials, same UVs, no draw-call change.
 * Face CENTRES are fixed points (u=0 ⇒ Y=±hy, v=0 ⇒ X=±hx), so a part keeps
 * its authored extents where it meets its neighbours — only corners pull in.
 *
 * Normals are corrected analytically (RoundedBoxGeometry is non-indexed, so
 * `computeVertexNormals` would facet every chamfer): with the in-plane
 * Jacobian J = [[fu, b],[c, gv]], n' = (J⁻¹)ᵀ·n, and the OUT-of-plane component
 * must be scaled by det for the shared normalize to be correct — the same trap
 * `agent/bench-avatar-shape.mjs` caught on the avatar version.
 */
function roundSectionXY(geo: THREE.BufferGeometry, w: number, h: number, s: number): void {
  if (s <= 0) return;
  const pos = geo.attributes.position, nor = geo.attributes.normal;
  const hx = w / 2, hy = h / 2;
  for (let i = 0; i < pos.count; i++) {
    const u = Math.max(-1, Math.min(1, pos.getX(i) / hx));
    const v = Math.max(-1, Math.min(1, pos.getY(i) / hy));
    const ru = Math.sqrt(Math.max(1e-6, 1 - (u * u) / 2));
    const rv = Math.sqrt(Math.max(1e-6, 1 - (v * v) / 2));
    const fu = 1 - s + s * rv;
    const fv = -s * u * v / (2 * rv);
    const gv = 1 - s + s * ru;
    const gu = -s * v * u / (2 * ru);
    pos.setX(i, hx * (u * (1 - s) + s * u * rv));
    pos.setY(i, hy * (v * (1 - s) + s * v * ru));
    const nx = nor.getX(i), ny = nor.getY(i), nz = nor.getZ(i);
    const b = (hx * fv) / hy, c = (hy * gu) / hx;
    const mx = gv * nx - c * ny;
    const my = fu * ny - b * nx;
    const mz = nz * (fu * gv - b * c);
    const len = Math.hypot(mx, my, mz) || 1;
    nor.setXYZ(i, mx / len, my / len, mz / len);
  }
  pos.needsUpdate = true;
  nor.needsUpdate = true;
}

/** Box with an elliptical XY section, cached by (w,h,d,s) like its siblings —
 *  a weapon swap re-uses the same GPU buffer. */
const sectionGeoCache = new Map<string, THREE.BufferGeometry>();
function sharedSectionGeo(w: number, h: number, d: number, s: number): THREE.BufferGeometry {
  if (s <= 0) return sharedBoxGeo(w, h, d);
  const key = `${w}|${h}|${d}|${s}`;
  let g = sectionGeoCache.get(key);
  if (!g) {
    const r = Math.min(0.045, Math.min(w, h, d) * 0.32);
    g = r > 0.012
      ? new RoundedBoxGeometry(w, h, d, 2, r)
      : new THREE.BoxGeometry(w, h, d, 2, 2, 1);
    roundSectionXY(g, w, h, s);
    g.userData.shared = true;
    sectionGeoCache.set(key, g);
  }
  return g;
}

/** A grip is the one part a hand closes around: taller than it is wide AND
 *  than it is deep. Drives both the moulded-polymer surface and the strongest
 *  section rounding — an oval grip is the single most legible "this is a real
 *  gun" cue on a low-poly weapon. */
function isGrip(part: Part): boolean {
  const [w, h, d] = part.s;
  return (part.t === "carbon" || part.c === GRIP) && h > w * 1.6 && h > d;
}

/** How far to bend a part's section toward an ellipse. */
function sectionBlend(part: Part): number {
  const [w, h, d] = part.s;
  if (isGrip(part)) return 0.9;                       // moulded polymer grip
  if (part.t === "vent" && h > w * 2) return 0.55;    // magazine body
  if (w < h * 0.35 && d > h * 2) return 0.8;          // blade → lens section
  // square-sectioned but too short to justify a real cylinder (the airzooka's
  // bucket, a revolver cylinder, a breech): bend it almost all the way instead
  if (Math.abs(w - h) < Math.max(w, h) * 0.3 && d > Math.max(w, h)) return 0.85;
  if (h < w * 0.4 || w < h * 0.25) return 0.25;       // flat rails/sights: chamfer only
  return 0.45;                                        // receivers, housings, stocks
}

function partGeometry(part: Part): THREE.BufferGeometry {
  const [w, h, d] = part.s;
  const longZ = d > Math.max(w, h) * 2.4;
  // A tube is a tube: any long part with a roughly square section is a barrel,
  // a shroud, a scope body or a suppressor. The old rule additionally required
  // one of eight authored colours, so identically-shaped parts on different
  // weapons disagreed about whether they were round.
  const squareish = Math.abs(w - h) < Math.max(w, h) * 0.3;
  const roundBarrel = longZ && (
    squareish ||
    part.t === "brushed" ||
    part.t === "chrome" ||
    part.t === "gold" ||
    part.t === "vent" ||
    part.c === DARK ||
    part.c === GUNMETAL ||
    part.c === "#8c99a6" ||
    part.c === "#cfd6e6"
  );
  return roundBarrel ? sharedCylinderZGeo(w, h, d) : sharedSectionGeo(w, h, d, sectionBlend(part));
}

/** The same shaped geometry at a build-time SCALE — the third-person held
 *  weapon (1.35x) and the deathmatch weapon-chest prop (1x, scaled by the
 *  group) both used raw boxes, so the gun in your hands was a machined,
 *  round-barrelled object and the identical gun in a remote player's hands was
 *  a pile of cubes. Called at build time only; the scratch `Part` never
 *  reaches a frame loop, and the geometry itself is still shared+cached. */
export function partGeometryFor(part: Part, scale = 1): THREE.BufferGeometry {
  if (scale === 1) return partGeometry(part);
  return partGeometry({
    ...part,
    s: [part.s[0] * scale, part.s[1] * scale, part.s[2] * scale],
  });
}

/** Deep-free a detached subtree, shared-resource aware: module-cached
 *  geometries (sharedBoxGeo) and textures (weapon patterns, toon gradient,
 *  the viewmodel skin) are marked userData.shared and skipped;
 *  material.dispose() never touches gradientMap so toonGradient() is safe. */
export function disposeDeep(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry && !m.geometry.userData?.shared) m.geometry.dispose?.();
    const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
    for (const mat of mats) {
      const t = mat as THREE.MeshStandardMaterial;
      if (t.map && !t.map.userData?.shared) t.map.dispose();
      mat.dispose();
    }
  });
}

/** Freeze a built weapon subtree's LOCAL matrices: parts never move after
 *  build, so composing position/quaternion/scale into `matrix` every frame is
 *  pure waste. Callers whose ROOT does move per frame (the avatar's held
 *  weapon follows aim pitch) must re-enable matrixAutoUpdate on the root. */
export function bakeStaticLocal(root: THREE.Object3D): void {
  root.traverse((o) => { o.matrixAutoUpdate = false; o.updateMatrix(); });
}

/** Show/hide a CACHED weapon build, matrix-pass aware. three's
 *  updateMatrixWorld recurses into invisible subtrees unconditionally
 *  (Object3D.js — the child loop has no visibility guard), so up to 16
 *  hidden cached builds under every moving avatar/camera were re-multiplying
 *  their world matrices every frame. Hidden ⇒ drop the whole subtree out of
 *  the pass (matrixWorldAutoUpdate=false); shown ⇒ re-enable and refresh
 *  IMMEDIATELY — same-frame readers (muzzleWorld tracer origins, the mirror
 *  render) must not see a matrixWorld frozen at the pose where the build was
 *  last hidden. */
export function setCachedShown(root: THREE.Object3D, shown: boolean): void {
  root.visible = shown;
  root.traverse((o) => { o.matrixWorldAutoUpdate = shown; });
  if (shown) root.updateWorldMatrix(true, true);
}

function inferredTexture(part: Part): TextureKey | null {
  if (part.t) return part.t;
  if (part.c === WOOD || part.c === "#5a4632") return "wood";
  if (part.c === HAZARD) return "hazard";
  if (part.c === ACCENT) return "plasma";
  if (part.c === VIOLET) return "violet";
  if (part.c === RED || part.c === "#ff4d6d") return "red";
  if (part.c === "#d7c07a" || part.c === "#fff1a6") return "gold";
  if (part.c === "#cfd6e6" || part.c === "#c8d0dc") return "chrome";
  if (part.c === DARK || part.c === "#11151d" || part.c === "#10141b" || part.c === "#05070a") return "vent";
  if (part.s[2] > 0.28) return "brushed";
  return null;
}

/** The surface family a part resolves to — the ONLY input (besides the part's
 *  authored colour and the third-person flag) that `materialForPart` varies on.
 *  Exported so a call site that merges parts can bucket by material WITHOUT
 *  building one material per part: two parts with the same (kind, colour) get a
 *  byte-identical material, so `weaponPartMatKey` is a safe merge key. */
export function partSurfaceKind(part: Part): WeaponSurface | "stipple" {
  return isGrip(part) ? "stipple" : (inferredTexture(part) ?? "plain");
}

/** A merge key: parts sharing it produce an identical `materialForPart`. */
export function weaponPartMatKey(part: Part, thirdPerson = false): string {
  return `${partSurfaceKind(part)}|${part.c}|${thirdPerson ? 1 : 0}`;
}

export function materialForPart(part: Part, thirdPerson = false): THREE.Material {
  // A grip's surface is decided by its SHAPE, not by the colour the part list
  // happened to author — every weapon's grip now wears moulded polymer stipple.
  const kind: WeaponSurface | "stipple" = partSurfaceKind(part);
  const glow = kind === "plasma" || kind === "violet" || kind === "grid";
  const params: THREE.MeshStandardMaterialParameters = {
    roughness: kind === "chrome" || kind === "gold" ? 0.32 : thirdPerson ? 0.58 : 0.48,
    metalness: kind === "wood" ? 0.05 : kind === "stipple" ? 0.06 : kind === "hazard" ? 0.25 : 0.45,
    emissive: glow ? part.c : "#000000",
    emissiveIntensity: glow ? 0.45 : 0,
  };
  // Relief strength per family: a machined fin is deep, a clear-coated weave is
  // shallow, and the third-person copy of the same gun is metres away, where a
  // full-strength normal map only reads as noise.
  const strength = (kind === "vent" ? 1.15 : kind === "stipple" ? 0.9 : kind === "chrome" ? 0.35 : 0.7)
    * (thirdPerson ? 0.7 : 1);
  return new THREE.MeshStandardMaterial(weaponMaps(kind, part.c, params, strength));
}

/** HOSE guns read as HUGE (owner 2026-10-28: "huge diameter, the gun itself
 *  and the jet"): fatten every part across the barrel axis (x/y) and stretch
 *  it a little along it (z). Applied to positions too so the parts stay joined. */
const HOSE_FAT = 7.2; // owner 2026-10-28: "3x the size" (was 2.4 / 1.45)
const HOSE_LONG = 4.35;
function hoseScaled(r: { parts: Part[]; muzzle: [number, number, number] }): { parts: Part[]; muzzle: [number, number, number] } {
  const sc = (v: [number, number, number]): [number, number, number] => [v[0] * HOSE_FAT, v[1] * HOSE_FAT, v[2] * HOSE_LONG];
  return { parts: r.parts.map((q) => ({ ...q, p: sc(q.p), s: sc(q.s) })), muzzle: sc(r.muzzle) };
}

/** Local-space part lists per weapon (z- = forward/muzzle direction). */
export function partsFor(id: string): { parts: Part[]; muzzle: [number, number, number] } {
  if (id === "penisgun" || id === "watergun") return hoseScaled(basePartsFor(id));
  return basePartsFor(id);
}

function basePartsFor(id: string): { parts: Part[]; muzzle: [number, number, number] } {
  switch (id) {
    case "rocketlauncher":
      return {
        parts: [
          { p: [0, 0, -0.1], s: [0.11, 0.11, 0.62], c: "#4a5340", t: "brushed" }, // launch tube
          { p: [0, 0, -0.26], s: [0.115, 0.115, 0.05], c: HAZARD, t: "hazard" }, // hazard warhead band
          { p: [0, 0, -0.42], s: [0.14, 0.14, 0.08], c: DARK, t: "vent" }, // muzzle ring
          { p: [0, 0, 0.22], s: [0.13, 0.13, 0.06], c: DARK, t: "vent" }, // exhaust bell
          { p: [0, 0, 0.26], s: [0.1, 0.1, 0.03], c: RED, t: "red" }, // hot exhaust glow
          { p: [0, -0.1, 0.02], s: [0.05, 0.11, 0.07], c: GRIP, t: "carbon" }, // grip
          { p: [0, -0.085, -0.22], s: [0.04, 0.08, 0.06], c: GRIP }, // front grip
          { p: [0, 0.09, -0.18], s: [0.02, 0.05, 0.14], c: "#9fb04a" }, // flip sight
          { p: [0, 0.09, -0.245], s: [0.016, 0.03, 0.012], c: ACCENT }, // sight glow bead
          { p: [0, 0.075, -0.06], s: [0.09, 0.02, 0.16], c: "#a53a2f", t: "red" }, // rocket tip peeking out
        ],
        muzzle: [0, 0, -0.5],
      };
    case "penisgun":
      // The PENIS GUN (mega_chibre powerup): a flesh-toned shaft barrel with a
      // pink bulbous glans at the muzzle, blue vein details, a grip, and two
      // "reservoir" balls at the base. Boxes only, viewmodel convention.
      return {
        parts: [
          { p: [0, 0, -0.02], s: [0.12, 0.12, 0.4], c: "#e0989e" },             // shaft barrel
          { p: [0, 0.006, -0.235], s: [0.15, 0.15, 0.11], c: "#d15b78" },       // glans (pink tip)
          { p: [0, 0.006, -0.3], s: [0.05, 0.05, 0.02], c: "#7a2530", t: "vent" }, // meatus / muzzle hole
          { p: [-0.05, 0.02, -0.02], s: [0.02, 0.02, 0.34], c: "#6f8fd6" },     // vein L (blue)
          { p: [0.045, 0.032, -0.05], s: [0.02, 0.02, 0.26], c: "#6f8fd6" },    // vein R
          { p: [0, -0.13, 0.1], s: [0.05, 0.12, 0.07], c: GRIP, t: "carbon" },  // pistol grip
          { p: [-0.06, -0.055, 0.19], s: [0.075, 0.075, 0.075], c: "#d98890" }, // ball L (reservoir)
          { p: [0.06, -0.055, 0.19], s: [0.075, 0.075, 0.075], c: "#d98890" },  // ball R
        ],
        muzzle: [0, 0.006, -0.31],
      };
    case "watergun":
      // WATER GUN: fireman-hose pipe with brass couplings, blue pressure tank
      // and a dark nozzle. Boxes only, matching the viewmodel convention.
      return {
        parts: [
          { p: [0, 0, -0.07], s: [0.13, 0.13, 0.5], c: "#8c99a6", t: "brushed" },   // metal hose pipe
          { p: [0, 0, -0.34], s: [0.17, 0.17, 0.08], c: "#c79a35" },                // brass nozzle ring
          { p: [0, 0, -0.4], s: [0.09, 0.09, 0.06], c: "#111820", t: "vent" },      // black outlet
          { p: [0, 0, 0.18], s: [0.2, 0.18, 0.14], c: "#167bd8", t: "brushed" },    // pressure tank
          { p: [0, 0.12, 0.07], s: [0.06, 0.05, 0.28], c: "#c79a35" },              // top handle
          { p: [0, -0.13, 0.05], s: [0.05, 0.13, 0.08], c: GRIP, t: "carbon" },     // grip
          { p: [-0.075, -0.02, -0.14], s: [0.035, 0.035, 0.28], c: "#35c4ff" },      // blue hose L
          { p: [0.075, -0.02, -0.14], s: [0.035, 0.035, 0.28], c: "#35c4ff" },       // blue hose R
        ],
        muzzle: [0, 0, -0.43],
      };
    case "fartblaster":
      // The AIRZOOKA (owner spec 2026-07-23): a blue cylindrical BUCKET as
      // the barrel, a dark circular outlet in its bottom (front), an elastic
      // membrane stretched over the open back, a pistol grip halfway along,
      // and an anti-aircraft-style metallic spiderweb/cross sight on top.
      // Boxes only (viewmodel convention — the rocket "tube" is a box too):
      // the bucket reads cylindrical via the rim/taper stacking.
      return {
        parts: [
          { p: [0, 0, -0.06], s: [0.17, 0.17, 0.34], c: "#2e7fff", t: "brushed" }, // bucket body
          { p: [0, 0, -0.24], s: [0.19, 0.19, 0.035], c: "#1b5fd0" },              // front rim
          { p: [0, 0, 0.115], s: [0.19, 0.19, 0.035], c: "#1b5fd0" },              // rear rim
          { p: [0, 0, -0.252], s: [0.075, 0.075, 0.02], c: "#05070a", t: "vent" }, // firing outlet hole
          { p: [0, 0, 0.135], s: [0.155, 0.155, 0.016], c: "#e9edf5" },            // elastic membrane (back)
          { p: [0, 0, 0.152], s: [0.032, 0.032, 0.024], c: DARK },                 // membrane pull knob
          { p: [0, -0.14, -0.06], s: [0.05, 0.13, 0.075], c: GRIP, t: "carbon" },  // pistol grip (halfway)
          { p: [0, 0.105, -0.09], s: [0.016, 0.05, 0.016], c: DARK },              // sight post
          // AA "spiderweb" sight: square outer frame + full cross, chrome
          { p: [0, 0.2, -0.09], s: [0.13, 0.012, 0.012], c: "#cfd6e6" },           // frame top
          { p: [0, 0.13, -0.09], s: [0.13, 0.012, 0.012], c: "#cfd6e6" },          // frame bottom
          { p: [-0.065, 0.165, -0.09], s: [0.012, 0.082, 0.012], c: "#cfd6e6" },   // frame left
          { p: [0.065, 0.165, -0.09], s: [0.012, 0.082, 0.012], c: "#cfd6e6" },    // frame right
          { p: [0, 0.165, -0.09], s: [0.11, 0.008, 0.008], c: "#aab4c4" },         // web spoke —
          { p: [0, 0.165, -0.09], s: [0.008, 0.07, 0.008], c: "#aab4c4" },         // web spoke |
          { p: [0, 0.165, -0.095], s: [0.016, 0.016, 0.008], c: ACCENT },          // center bead (glow)
        ],
        muzzle: [0, 0, -0.4],
      };
    case "knife":
      return {
        parts: [
          { p: [0, -0.02, -0.08], s: [0.03, 0.09, 0.09], c: GRIP, t: "carbon" }, // wrapped handle
          { p: [0, -0.065, -0.05], s: [0.026, 0.026, 0.026], c: ACCENT }, // glowing pommel gem
          { p: [0, 0.01, -0.155], s: [0.04, 0.024, 0.06], c: "#cfd6e6" }, // chrome guard
          { p: [0, 0.035, -0.29], s: [0.012, 0.05, 0.24], c: "#aab4c4" }, // blade
          { p: [0, 0.062, -0.29], s: [0.006, 0.008, 0.22], c: "#e2e9f4" }, // honed chrome edge
          { p: [0, 0.02, -0.2], s: [0.014, 0.014, 0.05], c: DARK }, // blood groove stub
        ],
        muzzle: [0, 0.035, -0.42],
      };
    case "pistol":
      return {
        parts: [
          { p: [0, 0.01, -0.1], s: [0.045, 0.075, 0.2], c: "#cfd6e6" }, // bright compact slide
          { p: [0, 0.01, 0.0], s: [0.048, 0.06, 0.06], c: DARK, t: "vent" }, // rear slide serrations
          { p: [0, -0.028, -0.12], s: [0.05, 0.02, 0.14], c: "#30384a" }, // two-tone frame rail
          { p: [0, -0.07, 0.015], s: [0.04, 0.105, 0.065], c: "#30384a", t: "carbon" }, // stippled grip
          { p: [0, -0.048, -0.065], s: [0.012, 0.045, 0.05], c: DARK }, // trigger guard
          { p: [0, 0.052, -0.15], s: [0.035, 0.016, 0.08], c: ACCENT }, // top neon sight
          { p: [0, 0.01, -0.225], s: [0.025, 0.03, 0.045], c: DARK }, // small muzzle
          { p: [0, 0.01, -0.207], s: [0.034, 0.038, 0.012], c: ACCENT }, // glowing muzzle ring
        ],
        muzzle: [0, 0.015, -0.25],
      };
    case "machine_pistol":
      return {
        parts: [
          { p: [0, 0, -0.13], s: [0.052, 0.088, 0.3], c: "#20252f", t: "carbon" }, // carbon body
          { p: [0.048, -0.005, -0.13], s: [0.012, 0.08, 0.24], c: HAZARD }, // hazard stripe R
          { p: [-0.048, -0.005, -0.13], s: [0.012, 0.08, 0.24], c: HAZARD }, // hazard stripe L
          { p: [0, -0.1, 0.0], s: [0.04, 0.125, 0.065], c: GRIP, t: "carbon" },
          { p: [0, -0.12, -0.16], s: [0.038, 0.18, 0.045], c: DARK, t: "vent" }, // long stick mag
          { p: [0, 0.02, -0.31], s: [0.03, 0.034, 0.075], c: "#4d5568", t: "vent" }, // vented comp
          { p: [0, 0.02, -0.352], s: [0.036, 0.04, 0.012], c: HAZARD }, // hazard comp tip
          { p: [0, -0.045, -0.29], s: [0.02, 0.024, 0.05], c: DARK }, // under-barrel laser box
          { p: [0, -0.045, -0.318], s: [0.012, 0.012, 0.01], c: RED }, // laser eye
          { p: [0, 0.06, -0.06], s: [0.02, 0.022, 0.022], c: ACCENT }, // rear dot sight
        ],
        muzzle: [0, 0.02, -0.36],
      };
    case "smg":
      return {
        parts: [
          { p: [0, 0.005, -0.18], s: [0.07, 0.095, 0.34], c: "#29313c", t: "carbon" }, // carbon shell
          { p: [-0.058, 0.01, -0.16], s: [0.012, 0.07, 0.2], c: "#ff4d6d" }, // red side rail L
          { p: [0.058, 0.01, -0.16], s: [0.012, 0.07, 0.2], c: "#ff4d6d" }, // red side rail R
          { p: [0, -0.095, 0.0], s: [0.05, 0.1, 0.07], c: GRIP, t: "carbon" },
          { p: [0, -0.02, 0.12], s: [0.03, 0.05, 0.14], c: DARK }, // folded skeleton stock
          { p: [0.055, -0.11, -0.17], s: [0.04, 0.15, 0.055], c: DARK, t: "vent" }, // side-mounted mag
          { p: [0.078, -0.1, -0.17], s: [0.008, 0.1, 0.03], c: "#ff4d6d", t: "red" }, // glowing ammo window
          { p: [0, 0.005, -0.39], s: [0.038, 0.045, 0.12], c: "#11151d", t: "vent" }, // ringed suppressor
          { p: [0, 0.005, -0.35], s: [0.045, 0.052, 0.014], c: DARK }, // suppressor collar
          { p: [0, -0.06, -0.28], s: [0.024, 0.06, 0.04], c: GRIP }, // stub foregrip
          { p: [0, 0.07, -0.08], s: [0.024, 0.035, 0.1], c: ACCENT }, // neon optic
        ],
        muzzle: [0, 0.005, -0.45],
      };
    case "pump_shotgun":
      return {
        parts: [
          { p: [0, 0.02, -0.25], s: [0.045, 0.055, 0.62], c: "#202632" }, // long single barrel
          { p: [0, 0.055, -0.28], s: [0.05, 0.014, 0.5], c: DARK, t: "vent" }, // ribbed heat shield
          { p: [0, -0.032, -0.24], s: [0.038, 0.035, 0.55], c: "#11151d" }, // tube magazine
          { p: [0, -0.07, -0.24], s: [0.07, 0.055, 0.22], c: WOOD }, // big sliding pump
          { p: [0, -0.07, -0.24], s: [0.075, 0.02, 0.23], c: "#cfd6e6" }, // chrome pump rail
          { p: [0, -0.08, 0.12], s: [0.055, 0.105, 0.16], c: WOOD }, // wooden stock
          { p: [0, -0.02, 0.14], s: [0.06, 0.02, 0.1], c: "#d7c07a" }, // brass butt plate
          { p: [0.042, 0.005, 0.0], s: [0.014, 0.05, 0.09], c: "#d7c07a", t: "gold" }, // shell saddle (3 brass)
          { p: [0, 0.02, -0.575], s: [0.052, 0.062, 0.014], c: "#d7c07a" }, // brass muzzle ring
          { p: [0, 0.07, -0.03], s: [0.015, 0.035, 0.08], c: HAZARD }, // bead sight
        ],
        muzzle: [0, 0.02, -0.58],
      };
    case "shotgun":
      return {
        parts: [
          { p: [-0.032, 0.015, -0.2], s: [0.032, 0.052, 0.5], c: "#3b2e23" }, // left barrel
          { p: [0.032, 0.015, -0.2], s: [0.032, 0.052, 0.5], c: "#3b2e23" }, // right barrel
          { p: [0, 0.015, -0.42], s: [0.085, 0.058, 0.02], c: "#d7c07a" }, // gold twin muzzle ring
          { p: [0, -0.018, -0.03], s: [0.08, 0.075, 0.24], c: "#1b202a" }, // chunky breech
          { p: [0, -0.018, -0.03], s: [0.084, 0.03, 0.06], c: "#d7c07a", t: "gold" }, // engraved gold band
          { p: [-0.025, 0.035, 0.075], s: [0.014, 0.045, 0.02], c: "#cfd6e6" }, // hammer spur L
          { p: [0.025, 0.035, 0.075], s: [0.014, 0.045, 0.02], c: "#cfd6e6" }, // hammer spur R
          { p: [0, -0.085, 0.08], s: [0.065, 0.11, 0.14], c: WOOD }, // sawed-off grip
          { p: [0, -0.13, 0.09], s: [0.068, 0.03, 0.145], c: RED, t: "red" }, // red leather wrap
        ],
        muzzle: [0, 0.015, -0.48],
      };
    case "carbine":
      return {
        parts: [
          { p: [0, 0, -0.22], s: [0.052, 0.09, 0.42], c: "#2d4955" }, // teal receiver
          { p: [0, 0.0, -0.38], s: [0.056, 0.07, 0.14], c: "#17252c", t: "vent" }, // vented shroud
          { p: [0, -0.01, 0.13], s: [0.03, 0.06, 0.14], c: "#17252c" }, // skeleton stock arm
          { p: [0, -0.01, 0.21], s: [0.03, 0.1, 0.03], c: "#2d4955" }, // stock butt
          { p: [0, -0.095, 0.03], s: [0.042, 0.11, 0.07], c: GRIP, t: "carbon" },
          { p: [0, -0.105, -0.16], s: [0.038, 0.12, 0.055], c: "#17252c" }, // mag
          { p: [0, -0.075, -0.3], s: [0.026, 0.07, 0.045], c: GRIP }, // angled foregrip
          { p: [0, 0.078, -0.12], s: [0.07, 0.025, 0.22], c: ACCENT }, // bright carry rail
          { p: [0, 0.078, -0.245], s: [0.02, 0.03, 0.02], c: "#9dff00" }, // front glow dot
          { p: [0, 0, -0.47], s: [0.026, 0.035, 0.11], c: DARK },
        ],
        muzzle: [0, 0, -0.5],
      };
    case "rifle":
      return {
        parts: [
          { p: [0, 0, -0.24], s: [0.065, 0.105, 0.52], c: "#21302a" }, // od-green receiver
          { p: [0, -0.02, 0.13], s: [0.045, 0.09, 0.18], c: "#21302a", t: "vent" }, // solid stock
          { p: [0, 0.045, -0.35], s: [0.02, 0.02, 0.28], c: "#10161c" }, // gas tube
          { p: [0, -0.1, 0.04], s: [0.045, 0.11, 0.07], c: "#232833", t: "carbon" },
          { p: [-0.045, -0.13, -0.11], s: [0.04, 0.17, 0.075], c: "#11151d", t: "vent" }, // offset curved mag
          { p: [0.048, 0.03, 0.02], s: [0.02, 0.024, 0.05], c: "#cfd6e6" }, // chrome charging handle
          { p: [0.038, 0.0, -0.06], s: [0.008, 0.02, 0.03], c: "#9dff00" }, // chamber-ready glow
          { p: [0, 0.078, -0.16], s: [0.04, 0.05, 0.18], c: HAZARD }, // hazard top rail
          { p: [0, -0.025, -0.49], s: [0.04, 0.06, 0.14], c: "#10161c", t: "vent" }, // vented brake
          { p: [0, 0.04, -0.48], s: [0.08, 0.012, 0.1], c: "#6be675" }, // green blade sight
        ],
        muzzle: [0, 0.005, -0.56],
      };
    case "burst_rifle":
      return {
        parts: [
          { p: [0, 0, -0.22], s: [0.052, 0.12, 0.48], c: "#3a2748", t: "carbon" }, // violet-carbon body
          { p: [0, 0.092, -0.16], s: [0.06, 0.06, 0.32], c: VIOLET }, // tall glowing carry handle
          { p: [0, -0.02, 0.14], s: [0.032, 0.07, 0.12], c: "#241733" }, // thumbhole stock
          { p: [0, -0.1, 0.02], s: [0.045, 0.11, 0.07], c: GRIP, t: "carbon" },
          { p: [0, -0.12, -0.12], s: [0.04, 0.13, 0.06], c: DARK, t: "vent" }, // mag
          { p: [0.032, 0.03, 0.0], s: [0.01, 0.014, 0.014], c: VIOLET }, // burst pip 1
          { p: [0.032, 0.03, -0.035], s: [0.01, 0.014, 0.014], c: VIOLET }, // burst pip 2
          { p: [0.032, 0.03, -0.07], s: [0.01, 0.014, 0.014], c: VIOLET }, // burst pip 3
          { p: [0, -0.01, -0.36], s: [0.05, 0.06, 0.12], c: "#241733", t: "vent" }, // vented handguard
          { p: [0, 0.0, -0.48], s: [0.07, 0.025, 0.055], c: VIOLET }, // flat glowing brake
        ],
        muzzle: [0, 0, -0.5],
      };
    case "lmg":
      return {
        parts: [
          { p: [0, 0, -0.24], s: [0.085, 0.13, 0.56], c: "#464029", t: "brushed" }, // heavy receiver
          { p: [0, -0.13, -0.06], s: [0.13, 0.14, 0.15], c: "#171a20", t: "hazard" }, // hazard ammo box
          { p: [0.075, -0.03, -0.02], s: [0.02, 0.06, 0.12], c: "#d7c07a", t: "gold" }, // brass belt feeding in
          { p: [0, -0.1, 0.06], s: [0.05, 0.11, 0.07], c: GRIP, t: "carbon" },
          { p: [0, -0.02, 0.15], s: [0.05, 0.09, 0.12], c: "#464029" }, // thick stock
          { p: [0, 0.01, -0.44], s: [0.055, 0.07, 0.2], c: "#171a20", t: "vent" }, // ribbed barrel shroud
          { p: [0.052, 0.02, -0.1], s: [0.01, 0.05, 0.14], c: RED, t: "red" }, // glowing heat vents
          { p: [0, 0.12, -0.02], s: [0.02, 0.05, 0.14], c: DARK }, // carry handle
          { p: [-0.045, -0.06, -0.46], s: [0.018, 0.11, 0.018], c: DARK }, // bipod legs
          { p: [0.045, -0.06, -0.46], s: [0.018, 0.11, 0.018], c: DARK },
          { p: [0, 0.075, -0.18], s: [0.09, 0.024, 0.26], c: HAZARD }, // heavy top rail
        ],
        muzzle: [0, 0.01, -0.52],
      };
    case "dmr":
      return {
        parts: [
          { p: [0, 0, -0.28], s: [0.045, 0.08, 0.66], c: "#24384a", t: "brushed" }, // navy receiver
          { p: [0, -0.005, -0.42], s: [0.05, 0.06, 0.22], c: "#141820", t: "vent" }, // vented handguard
          { p: [0, 0.092, -0.12], s: [0.05, 0.045, 0.24], c: "#080b10" }, // low scope
          { p: [0, 0.092, 0.02], s: [0.065, 0.055, 0.03], c: ACCENT }, // rear lens glow
          { p: [0, 0.092, -0.245], s: [0.055, 0.048, 0.014], c: "#9dff00" }, // front lens glint
          { p: [0, -0.02, 0.14], s: [0.04, 0.08, 0.14], c: "#24384a" }, // stock
          { p: [0, 0.04, 0.12], s: [0.036, 0.024, 0.1], c: DARK }, // cheek riser
          { p: [0, -0.09, 0.07], s: [0.042, 0.1, 0.09], c: GRIP, t: "carbon" },
          { p: [0.05, -0.102, -0.12], s: [0.034, 0.115, 0.045], c: DARK, t: "vent" }, // straight side mag
          { p: [0, -0.01, -0.64], s: [0.018, 0.028, 0.18], c: "#141820" }, // needle barrel
          { p: [0, -0.01, -0.71], s: [0.045, 0.014, 0.05], c: "#cfd6e6" }, // chrome brake fins
        ],
        muzzle: [0, -0.01, -0.73],
      };
    case "sniper":
      return {
        parts: [
          { p: [0, -0.005, -0.34], s: [0.048, 0.082, 0.78], c: "#151923", t: "brushed" }, // matte body
          { p: [0, 0.0, -0.55], s: [0.03, 0.04, 0.34], c: "#0c0f16", t: "vent" }, // fluted barrel
          { p: [0, 0.1, -0.12], s: [0.07, 0.065, 0.34], c: "#05070a" }, // oversized scope
          { p: [0, 0.1, -0.31], s: [0.085, 0.08, 0.035], c: "#ff4d6d", t: "red" }, // red objective lens
          { p: [0, 0.145, -0.12], s: [0.024, 0.024, 0.1], c: DARK }, // top rangefinder pod
          { p: [0, 0.145, -0.175], s: [0.014, 0.014, 0.012], c: RED }, // rangefinder eye
          { p: [0.05, 0.02, 0.04], s: [0.05, 0.02, 0.02], c: "#cfd6e6" }, // chrome bolt arm
          { p: [0.085, 0.0, 0.04], s: [0.026, 0.026, 0.026], c: "#cfd6e6", t: "chrome" }, // bolt knob
          { p: [0, -0.095, 0.1], s: [0.045, 0.11, 0.1], c: GRIP, t: "carbon" },
          { p: [0, -0.01, 0.16], s: [0.04, 0.095, 0.14], c: "#151923" }, // stock
          { p: [0, 0.05, 0.15], s: [0.034, 0.022, 0.1], c: "#2c3444" }, // cheek pad
          { p: [0, -0.01, 0.235], s: [0.045, 0.1, 0.02], c: RED }, // red recoil pad
          { p: [0, -0.105, -0.18], s: [0.034, 0.095, 0.05], c: DARK, t: "vent" }, // mag
          { p: [0, 0.01, -0.75], s: [0.07, 0.055, 0.075], c: DARK, t: "vent" }, // big vented brake
          { p: [-0.06, -0.05, -0.52], s: [0.014, 0.12, 0.014], c: "#5d6678" }, // bipod L
          { p: [0.06, -0.05, -0.52], s: [0.014, 0.12, 0.014], c: "#5d6678" }, // bipod R
        ],
        muzzle: [0, 0.01, -0.81],
      };
    case "magnum":
      return {
        parts: [
          { p: [0, 0.012, -0.16], s: [0.052, 0.065, 0.34], c: "#d7c07a", t: "gold" }, // engraved gold barrel
          { p: [0, 0.052, -0.16], s: [0.02, 0.018, 0.3], c: "#fff1a6" }, // top vent rib
          { p: [0, -0.03, -0.22], s: [0.018, 0.018, 0.18], c: "#cfd6e6" }, // chrome ejector rod
          { p: [0, -0.008, -0.02], s: [0.07, 0.088, 0.09], c: "#10141b", t: "chrome" }, // fluted cylinder
          { p: [0, -0.008, -0.02], s: [0.078, 0.03, 0.06], c: "#d7c07a", t: "gold" }, // cylinder gold band
          { p: [0, 0.045, 0.045], s: [0.014, 0.04, 0.03], c: "#cfd6e6" }, // hammer spur
          { p: [0, -0.092, 0.04], s: [0.044, 0.11, 0.07], c: WOOD }, // wood grip
          { p: [0, -0.15, 0.05], s: [0.048, 0.02, 0.075], c: "#fff1a6" }, // pearl grip cap
          { p: [0, 0.058, -0.28], s: [0.014, 0.028, 0.02], c: RED }, // red ramp front sight
        ],
        muzzle: [0, 0.02, -0.34],
      };
    case "ballistic":
      // spring-loaded knife launcher: a blade sits cocked on a rail — reads
      // unmistakably as the final tier, not another pistol
      return {
        parts: [
          { p: [0, -0.06, 0.0], s: [0.04, 0.1, 0.11], c: GRIP, t: "carbon" }, // grip
          { p: [0, 0.0, -0.08], s: [0.055, 0.06, 0.22], c: GUNMETAL, t: "brushed" }, // spring housing
          { p: [0, 0.0, -0.03], s: [0.062, 0.066, 0.016], c: VIOLET }, // spring coil ring 1
          { p: [0, 0.0, -0.08], s: [0.062, 0.066, 0.016], c: VIOLET }, // spring coil ring 2
          { p: [0, 0.0, -0.13], s: [0.062, 0.066, 0.016], c: VIOLET }, // spring coil ring 3
          { p: [0.032, 0.03, -0.05], s: [0.01, 0.024, 0.05], c: ACCENT }, // glowing tension gauge
          { p: [0, 0.005, -0.22], s: [0.04, 0.03, 0.12], c: DARK, t: "vent" }, // launch rail
          { p: [0, 0.03, -0.32], s: [0.014, 0.05, 0.26], c: "#aab4c4" }, // cocked blade
          { p: [0, 0.058, -0.32], s: [0.006, 0.008, 0.24], c: "#e2e9f4" }, // honed chrome edge
          { p: [0, 0.03, -0.47], s: [0.008, 0.032, 0.06], c: "#e2e9f4" }, // blade tip
          { p: [0, 0.055, -0.02], s: [0.016, 0.035, 0.09], c: ACCENT }, // sight fin
        ],
        muzzle: [0, 0.03, -0.52],
      };
    case "imperion":
      // THE IMPERION: a BIG full-neon beam cannon — thick glowing rail, huge
      // energy core, violet charge coils, wide emitter prongs. Reads as the
      // map's superweapon from across the arena.
      return {
        parts: [
          { p: [0, 0, -0.26], s: [0.085, 0.13, 0.78], c: GUNMETAL }, // massive rail body
          { p: [0, 0.005, -0.26], s: [0.095, 0.04, 0.7], c: ACCENT }, // full-length neon spine
          { p: [0.052, -0.01, -0.2], s: [0.02, 0.09, 0.5], c: VIOLET }, // violet side conduit R
          { p: [-0.052, -0.01, -0.2], s: [0.02, 0.09, 0.5], c: VIOLET }, // violet side conduit L
          { p: [0, -0.1, 0.06], s: [0.05, 0.13, 0.09], c: GRIP }, // grip
          { p: [0, -0.06, -0.34], s: [0.045, 0.09, 0.08], c: GRIP }, // fore grip
          { p: [0, -0.045, -0.1], s: [0.1, 0.09, 0.22], c: DARK }, // cell housing
          { p: [0, -0.045, -0.1], s: [0.108, 0.045, 0.16], c: ACCENT }, // GLOWING energy cell
          { p: [0, 0.095, -0.16], s: [0.05, 0.06, 0.3], c: DARK }, // optic tube
          { p: [0, 0.095, -0.33], s: [0.065, 0.07, 0.025], c: ACCENT }, // big lens ring
          { p: [0, 0.02, -0.5], s: [0.07, 0.07, 0.1], c: VIOLET }, // charge coil block
          { p: [0.045, 0, -0.72], s: [0.02, 0.05, 0.18], c: DARK }, // muzzle prong R
          { p: [-0.045, 0, -0.72], s: [0.02, 0.05, 0.18], c: DARK }, // muzzle prong L
          { p: [0.045, 0, -0.72], s: [0.024, 0.02, 0.12], c: ACCENT }, // prong glow R
          { p: [-0.045, 0, -0.72], s: [0.024, 0.02, 0.12], c: ACCENT }, // prong glow L
          { p: [0, 0, -0.7], s: [0.028, 0.028, 0.14], c: ACCENT }, // beam emitter core
        ],
        muzzle: [0, 0, -0.8],
      };
    default:
      return basePartsFor("pistol");
  }
}

const HIP_POS = new THREE.Vector3(0.26, -0.22, -0.45);
const ADS_POS = new THREE.Vector3(0, -0.14, -0.38);
const KNIFE_POS = new THREE.Vector3(0.3, -0.26, -0.42);

export class ViewModel {
  private group = new THREE.Group();
  private weaponGroup: THREE.Group | null = null;
  private muzzleLocal = new THREE.Vector3();
  private flash: THREE.Mesh;
  /** The 90°-crossed twin of `flash` — a child of it, so it inherits the roll
   *  and scale and costs no extra per-frame work. */
  private flashCross: THREE.Mesh;
  private muzzleLight!: THREE.PointLight;
  private flashUntil = 0;
  private currentId = "";
  private skinBody = "#232833";
  private skinGlow = "#00f0ff";
  private skinTex: THREE.Texture | null = null;
  /** URL the current skinTex was loaded from ("" = none) — reload detection. */
  private skinUrlCur = "";
  /** Built weapon groups, kept attached (hidden) — a Gun Game swap is every
   *  kill, so swapping is a visibility toggle, not a rebuild+dispose. */
  private weaponCache = new Map<string, { group: THREE.Group; muzzle: THREE.Vector3 }>();

  private skinSig = "";

  /** Dress the first-person arms in MY cosmetics (skin picture > body color).
   *  Idempotent — called per frame; rebuilds only on an actual change.
   *  The sig carries the full skin URL (not just its presence) so editing your
   *  skin mid-session actually reaches the arms — presence-only was a recorded
   *  defect: a changed URL never invalidated the cache. */
  setSkin(bodyColor?: string, glowColor?: string, skinUrl?: string): void {
    const sig = `${bodyColor}|${glowColor}|${skinUrl ?? ""}`;
    if (sig === this.skinSig) return;
    this.skinSig = sig;
    if (bodyColor) this.skinBody = bodyColor;
    if (glowColor) this.skinGlow = glowColor;
    if ((skinUrl ?? "") !== this.skinUrlCur) {
      this.skinUrlCur = skinUrl ?? "";
      // marked userData.shared, so disposeDeep skips it — dispose by hand here,
      // AFTER which every cached build referencing it is disposed below anyway
      this.skinTex?.dispose();
      this.skinTex = null;
      if (skinUrl) {
        this.skinTex = new THREE.TextureLoader().load(skinUrl, warmTexture);
        this.skinTex.colorSpace = THREE.SRGBColorSpace;
        this.skinTex.userData.shared = true; // one texture across every cached build
      }
    }
    // the arm materials bake the skin — every cached build is now stale
    for (const { group } of this.weaponCache.values()) {
      this.group.remove(group);
      disposeDeep(group);
    }
    this.weaponCache.clear();
    this.weaponGroup = null;
    const cur = this.currentId;
    this.currentId = ""; // force rebuild with the new arm materials
    if (cur) this.buildWeapon(cur);
  }
  private swapT = 1; // 0..1 raise animation
  private swingT = 1; // knife swing progress
  private tauntT = 1; // 0..1 taunt/moquerie gesture progress
  private meleeUntil = 0; // while > now, the knife is force-shown for a stab
  private pendingWeapon: string | null = null;
  private kickZ = 0;
  private kickRoll = 0;
  private bobT = 0;
  private breathT = 0;
  private moveLean = 0;
  private adsBlend = 0;
  private kickPitch = 0;
  private kickY = 0;
  private hoseOn = false;
  private hoseHz = 0;
  private hosePh = 0;
  private hoseAmt = 0;
  /** LOOK-SWAY (2026-08-08, realism pass): the weapon lags a beat behind the
   *  camera when you turn — the gun was hard-welded to the view before, the
   *  biggest "floating gun" tell in first person. Smoothed angular velocities. */
  private swayYaw = 0;
  private swayPitch = 0;
  private lastLookYaw = 0;
  private lastLookPitch = 0;
  private lookInit = false;
  /** Vertical-velocity weapon drift (rises while falling, presses down on a
   *  jump) + landing dip impulse fed by match on grounded transitions. */
  private airDrift = 0;
  private landT = 0;
  baseFov = 90;
  /** Extra FOV while sprinting (speed sensation); lerped via targetFov. */
  sprintFovBoost = 0;
  /** Additive FOV widen (kart speed sensation) — one authority owns camera.fov
   *  so nothing fights this lerp (that caused a "weird zoom" while aiming). */
  extraFov = 0;
  /** Absolute FOV override (IMPERION scope = baseFov/3); null = use ADS zoom. */
  overrideFov: number | null = null;

  /** `layer`: which render layer this viewmodel lives on. 2 = the main
   *  player's (mirror cams skip it). MULTIPLAYER LOCAL seats pass 3+slot so
   *  each seat's camera sees ITS OWN arms and nobody else's. */
  constructor(private camera: THREE.PerspectiveCamera, private layer = 2) {
    camera.add(this.group);
    // small fill light so the weapon reads even in dark arenas
    const fill = new THREE.PointLight("#cfe0ff", 2.2, 2.5);
    fill.position.set(0.1, 0.15, -0.1);
    this.group.add(fill);
    this.group.scale.setScalar(0.66);
    // MUZZLE FLASH (2026-08-01, the combat-FX pass). Was: one untextured
    // 0.14 m square with a solid `#ffaa2e` and a random Z roll — at the centre
    // of the screen, 75 ms at a time, a glowing postage stamp. Rolling a SQUARE
    // is also invisible, so the randomisation bought nothing.
    //
    // Now: the procedural 9-petal star, on TWO quads crossed at 90° about the
    // barrel axis. The cross is what gives the flash VOLUME — a single quad is
    // a decal pinned to the barrel and collapses to a line as the weapon swings
    // across the view during recoil, which is exactly when the flash is on
    // screen. Two draw calls, and only while `flash.visible`.
    const flashGeo = new THREE.PlaneGeometry(0.34, 0.34);
    const flashMat = new THREE.MeshBasicMaterial({
      color: "#ffd9a0", // the fire colour now lives in the texture
      map: muzzleFlashTexture(),
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.flash = new THREE.Mesh(flashGeo, flashMat);
    this.flash.visible = false;
    this.flashCross = new THREE.Mesh(flashGeo, flashMat);
    this.flashCross.rotation.z = Math.PI / 2; // same star, rolled 90° in its own plane
    this.flashCross.rotation.y = Math.PI / 2; // ...and turned to face across the barrel
    this.flash.add(this.flashCross);
    this.group.add(this.flash);

    // dynamic muzzle light — briefly illuminates the scene on each shot
    this.muzzleLight = new THREE.PointLight("#ffa030", 0, 14, 2);
    this.group.add(this.muzzleLight);

    // the whole viewmodel lives on layer 2: rendered by the MAIN camera only.
    // Secondary cameras (the practice-range mirrors) skip it — the reflection
    // shows the real body's arms, not these floating FP arms on top of them.
    camera.layers.enable(this.layer);
    this.group.traverse((o) => o.layers.set(this.layer));
  }

  /** During a quick-melee the knife is shown; defer real weapon swaps. */
  /** GHOST cloak: the first-person arms + weapon go see-through so your own
   *  view matches the near-invisibility everyone else sees. */
  private cloaked = false;
  setCloaked(on: boolean): void {
    if (on === this.cloaked) return;
    this.cloaked = on;
    this.applyCloak();
  }
  private applyCloak(): void {
    this.group.traverse((o) => {
      // any material-bearing object: meshes AND the outline LineSegments —
      // the old `isMesh` gate left the 0.6-opacity sketch lines uncloaked, so
      // at gfx L0 your "invisible" gun still wore fully-drawn dark outlines
      const m = o as THREE.Mesh;
      const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
      for (const mat of mats) {
        const std = mat as THREE.MeshStandardMaterial;
        // record the TRUE base state before mutating — recording after (and
        // gating on !cloaked) stamped wasTransparent=false on any genuinely
        // transparent material first seen mid-cloak (the 0.6-opacity outline
        // lines), so uncloaking rendered it fully opaque forever.
        if (std.userData.baseOpacity === undefined) { std.userData.baseOpacity = std.opacity; std.userData.wasTransparent = std.transparent; }
        std.transparent = this.cloaked ? true : std.userData.wasTransparent === true;
        std.opacity = this.cloaked ? Math.min(0.22, (std.userData.baseOpacity as number)) : (std.userData.baseOpacity as number);
        std.needsUpdate = true;
      }
    });
  }

  setWeapon(id: string): void {
    if (performance.now() < this.meleeUntil) { this.pendingWeapon = id; return; }
    this.buildWeapon(id);
    if (this.cloaked) this.applyCloak(); // fresh materials must go see-through too
  }

  /** Quick-melee: swap to the knife model + stab, then restore the gun. */
  meleeStab(): void {
    if (this.currentId !== "knife") this.pendingWeapon = this.currentId;
    this.buildWeapon("knife");
    this.swing();
    this.meleeUntil = performance.now() + 420;
  }

  private buildWeapon(id: string): void {
    const effective = id;
    if (effective === this.currentId) return;
    this.currentId = effective;
    // swap = hide the old build, show the cached one. Weapon models never
    // change for a given skin, and Gun Game swaps every kill (plus 2 per
    // quick-melee) — the old build-and-dispose here allocated ~15-30 meshes,
    // materials AND geometries per swap, exactly at kill moments.
    if (this.weaponGroup) setCachedShown(this.weaponGroup, false);
    const cached = this.weaponCache.get(effective);
    if (cached) {
      setCachedShown(cached.group, true);
      this.weaponGroup = cached.group;
      this.muzzleLocal.copy(cached.muzzle);
      this.swapT = 0; // raise animation still plays on every swap
      return;
    }
    const { parts, muzzle } = partsFor(effective);
    const wg = new THREE.Group();
    // ---- ARMS: forearms + gloved hands gripping the weapon (the gun no
    // longer floats). Suit sleeves + neon glove cuffs match the game look.
    // The arms are the biggest thing on screen after the gun and they were flat
    // MeshLambert colour fields — no grain, no gloss, no light response. They
    // now wear the SAME procedural suit/glove surfaces as the third-person body
    // (2026-08-01, `graphics`), so the hands you look at all match survive a
    // close-up. A drawn custom skin still wins over the suit weave.
    const sleeve = this.skinTex
      ? new THREE.MeshStandardMaterial({ map: this.skinTex, color: "#ffffff", roughness: 0.85, metalness: 0.04 })
      : new THREE.MeshStandardMaterial(avatarMaps("suit", { color: this.skinBody, roughness: 0.86, metalness: 0.08 }));
    // the hands wear `glove` (sewn panels, padded palm, knurled grip print) —
    // `gear` is a BOOT SOLE, and its 32 px moulded ribs read as corrugated
    // cardboard on a hand this close to the lens (2026-08-01)
    const glove = new THREE.MeshStandardMaterial(avatarMaps("glove", { color: "#171b24", roughness: 0.5, metalness: 0.2 }));
    const cuff = new THREE.MeshStandardMaterial({ color: this.skinGlow, emissive: this.skinGlow, emissiveIntensity: 0.6, roughness: 0.4 });
    const arm = (hx: number, hy: number, hz: number, back: [number, number, number]): void => {
      // the character's REAL arm: hand at the grip, forearm to an elbow, and an
      // upper arm continuing down toward the (off-screen) shoulder — one
      // connected limb in the player's skin, not a floating stub.
      // ONE merged fist — four fingers with two phalanges each, an opposed
      // two-segment thumb, a knuckle ridge and a cuff (see fpHandGeo). Replaces
      // three axis-aligned boxes with one draw call and a real silhouette.
      const hand = new THREE.Mesh(fpHandGeo(hx >= 0 ? 1 : -1), glove);
      hand.position.set(hx, hy, hz);
      // cant the wrist so the fingers close AROUND the grip instead of pointing
      // straight down past it
      hand.rotation.set(-0.22, hx >= 0 ? -0.12 : 0.12, hx >= 0 ? 0.16 : -0.16);
      const elbow = new THREE.Vector3(hx + back[0], hy + back[1], hz + back[2]);
      // forearm spans hand → elbow exactly, THICKENING toward the elbow
      const foreLen = elbow.distanceTo(new THREE.Vector3(hx, hy, hz));
      // Slimmed 2026-08-06 with the third-person body: a 0.25 m-thick forearm
      // is 1.8x the modelled arm (ARM_W 0.155) and was the FP half of the
      // "stacked blocks" read. 0.70x on both bones and on the cuff ring.
      const fore = new THREE.Mesh(sharedTaperedZGeo(0.055, 0.088, foreLen + 0.06), sleeve);
      fore.position.set(hx + back[0] / 2, hy + back[1] / 2, hz + back[2] / 2);
      fore.lookAt(elbow);
      // upper arm: elbow → down/back toward the shoulder, exits the frame,
      // thickening again toward the (off-screen) deltoid
      const shoulder = elbow.clone().add(new THREE.Vector3(back[0] * 0.9, -0.34, 0.34));
      const upLen = shoulder.distanceTo(elbow);
      const upper = new THREE.Mesh(sharedTaperedZGeo(0.076, 0.107, upLen + 0.06), sleeve);
      upper.position.copy(elbow.clone().add(shoulder).multiplyScalar(0.5));
      upper.lookAt(shoulder);
      const ring = new THREE.Mesh(sharedBoxGeo(0.095, 0.095, 0.032), cuff);
      ring.position.set(hx + back[0] * 0.22, hy + back[1] * 0.22, hz + back[2] * 0.22);
      wg.add(hand, fore, upper, ring);
    };
    const isBlade = effective === "knife";
    // right hand on the main grip (grips sit ~[0,-0.09,0.03] on most parts)
    arm(0.01, -0.1, 0.05, [0.1, -0.14, 0.3]);
    // left support hand on the fore-end of two-handed weapons
    if (!isBlade && effective !== "pistol" && effective !== "magnum" && effective !== "machine_pistol") {
      arm(-0.02, -0.045, -0.22, [-0.12, -0.16, 0.26]);
    }
    // The 3x HOSE guns (hoseScaled) reach ~1 m BEHIND their grip, i.e. through
    // the camera: slide them forward until the back end sits just ahead of the
    // eye, and drop them low-right so the barrel doesn't swallow the crosshair.
    let ox = 0, oy = 0, oz = 0;
    if (effective === "penisgun" || effective === "watergun") {
      let rear = -Infinity;
      for (const q of parts) rear = Math.max(rear, q.p[2] + q.s[2] / 2);
      oz = Math.min(0, 0.15 - rear);
      ox = 0.1; oy = -0.3;
    }
    for (const part of parts) {
      const mesh = new THREE.Mesh(
        partGeometry(part),
        materialForPart(part),
      );
      mesh.position.set(part.p[0] + ox, part.p[1] + oy, part.p[2] + oz);
      wg.add(mesh);
    }
    this.muzzleLocal.set(muzzle[0] + ox, muzzle[1] + oy, muzzle[2] + oz);
    this.weaponCache.set(effective, { group: wg, muzzle: this.muzzleLocal.clone() });
    this.weaponGroup = wg;
    this.group.add(wg);
    wg.traverse((o) => o.layers.set(this.layer)); // own-camera-only (mirrors skip viewmodels)
    bakeStaticLocal(wg); // parts never move locally; wg itself is camera-fixed
    wg.updateWorldMatrix(true, true); // current matrixWorld for same-frame readers
    this.swapT = 0; // play raise animation
  }

  /** Build every weapon model into the cache under the load screen, so every
   *  mid-match swap — including the FIRST use of each ladder tier — is a
   *  visibility toggle. ~17 tiny box-mesh builds, load-time only. */
  prewarm(): void {
    const cur = this.currentId;
    const swapT = this.swapT;
    // "rocketlauncher" is a pickup, not a WEAPONS ladder entry — without it
    // the first rocket grab mid-match was still a cold build.
    for (const id of [...Object.keys(WEAPONS), "rocketlauncher"]) this.buildWeapon(id);
    if (this.weaponGroup) setCachedShown(this.weaponGroup, false);
    this.weaponGroup = null;
    this.currentId = "";
    if (cur) this.buildWeapon(cur);
    this.swapT = swapT; // prewarm must not replay the raise animation
    if (this.cloaked) this.applyCloak(); // fresh builds must match cloak state
  }

  /** World-space muzzle position (for tracers). */
  muzzleWorld(out: THREE.Vector3): THREE.Vector3 {
    out.copy(this.muzzleLocal);
    if (this.weaponGroup) this.weaponGroup.localToWorld(out);
    else this.camera.localToWorld(out);
    return out;
  }

  /** Camera-space recoil the caller should add to aim (pitch kick). */
  fire(cfg: WeaponConfig, shotIndex: number): { pitch: number; yaw: number } {
    this.flashUntil = performance.now() + 75;
    this.muzzleLight.position.copy(this.muzzleLocal);
    this.muzzleLight.intensity = 15;
    // punchier recoil: barrel flips up (kickPitch), gun rises (kickY) and
    // recoils back toward the camera (kickZ), then snaps back.
    const heavy = cfg.damage > 45 || (cfg.pellets ?? 1) > 1;
    this.kickPitch = Math.min(0.11, this.kickPitch + (heavy ? 0.055 : 0.035));
    this.kickY = Math.min(0.05, this.kickY + 0.025);
    this.kickZ = Math.min(0.08, this.kickZ + (heavy ? 0.055 : 0.035));
    this.kickRoll = (Math.random() - 0.5) * (heavy ? 0.06 : 0.03);
    const pattern = cfg.recoilPattern;
    if (pattern.length === 0) return { pitch: 0, yaw: 0 };
    const k = pattern[Math.min(shotIndex, pattern.length - 1)];
    return { pitch: k.pitch, yaw: k.yaw };
  }

  swing(): void {
    this.swingT = 0;
  }

  /** Moquerie: dip + twirl the gun so the taunt is visible in first person. */
  taunt(): void {
    this.tauntT = 0;
  }

  /** Landing kick — called by match on a grounded transition after a fall. */
  land(intensity: number): void {
    this.landT = Math.min(1, this.landT + intensity);
  }

  /** Local hose firing state, set every frame (rate = the stream's spurt Hz). */
  setHose(on: boolean, hz: number): void { this.hoseOn = on; if (on) this.hoseHz = hz; }

  update(dt: number, moving: boolean, onGround: boolean, ads: boolean, lookYaw = 0, lookPitch = 0, vy = 0): void {
    // NB: the per-part black "sketch" outline that used to ride on every
    // weapon part (one extra LineSegments draw call each, ~10-16 per weapon,
    // shown at cost-ladder L0) is GONE as of 2026-08-01. A cel outline drawn
    // around a physically-shaded, normal-mapped gun reads as a sticker — the
    // same argument that removed the avatar's inverted-hull outlines — and the
    // remote copy of the very same weapon never had one, so your gun and
    // everyone else's disagreed about the art direction.
    // restore the real weapon once the quick-melee stab finishes
    if (this.meleeUntil && performance.now() >= this.meleeUntil) {
      this.meleeUntil = 0;
      if (this.pendingWeapon !== null) { this.buildWeapon(this.pendingWeapon); this.pendingWeapon = null; }
    }
    const isKnife = this.currentId === "knife";
    // 1-exp gain, not min(1, dt*k): the lerp form converges ~20% faster at
    // 30 fps than 144 — ADS feel should not depend on the monitor (same
    // defect class as the crouch-eye snap, milder; the kicks below already
    // use the exp idiom)
    this.adsBlend += ((ads && !isKnife ? 1 : 0) - this.adsBlend) * (1 - Math.exp(-dt * 10));
    this.swapT = Math.min(1, this.swapT + dt * 4);
    this.swingT = Math.min(1, this.swingT + dt * 5);
    this.tauntT = Math.min(1, this.tauntT + dt * 1.4);
    this.kickPitch *= Math.exp(-dt * 15);
    this.kickY *= Math.exp(-dt * 15);
    this.kickZ *= Math.exp(-dt * 16);
    this.kickRoll *= Math.exp(-dt * 14);

    // look-sway: smoothed angular velocity of the camera, nearly locked out
    // while aiming down sights (a scoped rifle should feel braced)
    let dYaw = 0, dPitch = 0;
    if (this.lookInit) {
      dYaw = lookYaw - this.lastLookYaw;
      if (dYaw > Math.PI) dYaw -= Math.PI * 2; else if (dYaw < -Math.PI) dYaw += Math.PI * 2;
      dPitch = lookPitch - this.lastLookPitch;
    }
    this.lastLookYaw = lookYaw; this.lastLookPitch = lookPitch; this.lookInit = true;
    const yawVel = Math.max(-5, Math.min(5, dYaw / Math.max(dt, 1 / 240)));
    const pitchVel = Math.max(-5, Math.min(5, dPitch / Math.max(dt, 1 / 240)));
    const swayGain = 1 - this.adsBlend * 0.85;
    this.swayYaw += (yawVel * swayGain - this.swayYaw) * (1 - Math.exp(-dt * 9));
    this.swayPitch += (pitchVel * swayGain - this.swayPitch) * (1 - Math.exp(-dt * 9));
    // vertical drift: the gun floats up a touch while falling, presses down on
    // the way up — and dips hard on the landing impulse from match
    this.airDrift += (Math.max(-12, Math.min(12, onGround ? 0 : vy)) - this.airDrift) * (1 - Math.exp(-dt * 7));
    this.landT *= Math.exp(-dt * 8);
    if (this.landT < 0.003) this.landT = 0;

    this.breathT += dt * (ads ? 0.85 : 1.15);
    this.moveLean += ((moving && onGround ? 1 : 0) - this.moveLean) * (1 - Math.exp(-dt * 8));
    if (moving && onGround) this.bobT += dt * (ads ? 4 : 8);
    const bobAmp = (1 - this.adsBlend) * (moving && onGround ? 1 : 0);
    const walkRoll = Math.sin(this.bobT) * 0.045 * bobAmp;
    const walkYaw = Math.sin(this.bobT * 0.5) * 0.025 * bobAmp;
    const bobX = Math.sin(this.bobT) * 0.012 * bobAmp;
    const bobY = Math.abs(Math.cos(this.bobT)) * 0.012 * bobAmp;
    const breath = Math.sin(this.breathT * Math.PI * 2) * (ads ? 0.0025 : 0.0045) * (1 - this.moveLean * 0.45);

    const base = isKnife ? KNIFE_POS : HIP_POS;
    // scalar lerp, not `new THREE.Vector3().lerpVectors(...)` — update() runs
    // every frame for every player, so that was one guaranteed heap allocation
    // per frame on the hottest path in the game (render-loop budget rule).
    const t = this.adsBlend;
    const targetX = base.x + (ADS_POS.x - base.x) * t;
    const targetY = base.y + (ADS_POS.y - base.y) * t;
    const targetZ = base.z + (ADS_POS.z - base.z) * t;
    // raise-from-below on swap
    const raise = (1 - this.swapT) * -0.25;
    this.group.position.set(
      targetX + bobX + this.swayYaw * 0.004,
      targetY + bobY + breath + raise + this.kickY - this.airDrift * 0.0032 - this.landT * 0.05 - this.swayPitch * 0.0025,
      targetZ);

    // knife swing: quick forward arc
    let swingRot = 0;
    let swingPush = 0;
    if (this.swingT < 1) {
      const t = this.swingT;
      const arc = Math.sin(t * Math.PI);
      swingRot = -arc * 1.1;
      swingPush = -arc * 0.22;
    }
    // taunt gesture: lower the gun and give it a cocky twirl/wave
    let tauntDrop = 0, tauntRoll = 0, tauntYaw = 0;
    if (this.tauntT < 1) {
      const a = Math.sin(this.tauntT * Math.PI); // 0→1→0 envelope
      tauntDrop = a * 0.16;
      tauntRoll = Math.sin(this.tauntT * Math.PI * 3) * 0.5 * a;
      tauntYaw = Math.sin(this.tauntT * Math.PI * 2) * 0.25 * a;
    }
    this.group.position.y -= tauntDrop;
    this.group.rotation.set(
      this.kickPitch * 2.4 + swingRot + Math.abs(Math.sin(this.bobT)) * 0.018 * bobAmp - this.swayPitch * 0.012 - this.landT * 0.12,
      0.05 * (1 - this.adsBlend) + tauntYaw + walkYaw - this.swayYaw * 0.014,
      this.kickRoll + tauntRoll + walkRoll + this.swayYaw * 0.006,
    );
    this.group.position.z += swingPush + this.kickZ; // recoil back toward camera
    // HOSE THROB: while a hose fires, the gun bucks in time with the stream's
    // spurts (owner: "make the animation of the sperm gun and water better")
    this.hoseAmt += ((this.hoseOn ? 1 : 0) - this.hoseAmt) * Math.min(1, dt * 12);
    if (this.hoseAmt > 0.002) {
      this.hosePh += dt * this.hoseHz;
      const sp = Math.sin(this.hosePh * Math.PI * 2);
      const buck = this.hoseAmt * (sp > 0 ? sp * sp : 0);
      this.group.position.z += buck * 0.035;
      this.group.position.y += buck * 0.012 + this.hoseAmt * Math.sin(this.hosePh * 17.3) * 0.0025;
      this.group.position.x += this.hoseAmt * Math.sin(this.hosePh * 11.1) * 0.0025;
      this.group.rotation.x += buck * 0.035;
    }

    // muzzle light decays fast after each shot
    if (this.muzzleLight.intensity > 0.01) {
      this.muzzleLight.intensity *= Math.exp(-dt * 40);
    } else {
      this.muzzleLight.intensity = 0;
    }

    // muzzle flash
    if (this.flash.visible = performance.now() < this.flashUntil) {
      this.flash.position.copy(this.muzzleLocal);
      this.flash.rotation.z = Math.random() * Math.PI;
      // COLLAPSE over the flash's 75 ms: hot and wide on the first frame, then
      // shrinking and dimming. The old flash held one random size for its whole
      // life, so it strobed rather than decayed.
      const k = Math.max(0, Math.min(1, (this.flashUntil - performance.now()) / 75));
      const s = (0.45 + 0.75 * k) * (0.85 + Math.random() * 0.3);
      this.flash.scale.set(s, s, s);
      (this.flash.material as THREE.MeshBasicMaterial).opacity = 0.35 + 0.6 * k;
    }

    // scope view: the gun disappears while you look through the sniper lens
    // (per-frame caller — only touch the matrix-pass flags on a real change)
    if (this.weaponGroup && this.weaponGroup.visible === this.scopeOn) {
      setCachedShown(this.weaponGroup, !this.scopeOn);
    }

    // ADS FOV zoom (relative to the player's configured base FOV) — the
    // sniper zooms HARD (down to ~16°, a real scope), everything else gets
    // the mild 20° tightening
    // sniper scope = exactly 3× magnification (fov/3); others mild tighten
    const zoomDepth = this.currentId === "sniper" ? this.baseFov * (2 / 3) : 20;
    // ONE place computes the target: absolute override (IMPERION scope) wins,
    // otherwise ADS zoom + sprint + kart-speed widen. camera.fov is written
    // here and NOWHERE else — a second writer caused the aiming-zoom wobble.
    const targetFov = this.overrideFov != null
      ? this.overrideFov
      : this.baseFov - this.adsBlend * zoomDepth + this.sprintFovBoost + this.extraFov;
    if (Math.abs(this.camera.fov - targetFov) > 0.05) {
      // 1-exp gain: frame-rate-independent zoom rate (see adsBlend above)
      this.camera.fov += (targetFov - this.camera.fov) * (1 - Math.exp(-dt * 12));
      this.camera.updateProjectionMatrix();
    }
  }

  /** True while looking through the sniper scope (drives the lens overlay
   *  and the scoped look-sensitivity drop). */
  get scopeOn(): boolean {
    return this.currentId === "sniper" && this.adsBlend > 0.6;
  }

  /** Hide the first-person weapon (e.g. while driving a vehicle). */
  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  dispose(): void {
    this.camera.remove(this.group);
    // free every cached build's materials (shared geometries/textures survive
    // by design — the module caches own them for the tab's lifetime)
    for (const { group } of this.weaponCache.values()) disposeDeep(group);
    this.weaponCache.clear();
    this.weaponGroup = null;
  }
}
